import { useFragment } from "@/generated";
import { MoveSlidesToTrashDocument, SlideFieldsFragmentDoc } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for moving slides to trash and updating the Apollo cache.
 *
 * Provides a mutation function to move one or more slides to trash.
 * Automatically updates the Apollo cache to reflect the "deleted" status of each slide.
 * This ensures the UI immediately reflects the changes without requiring a full refetch.
 *
 * @returns An object containing:
 *   - moveSlidesToTrash: Async function to move slides to trash. Takes an array of slideIds to move to trash, returns the updated slides.
 *   - loading: Boolean indicating if the mutation is in progress
 *
 * @example
 * const { moveSlidesToTrash, loading } = useMoveSlidesToTrash();
 * const updatedSlides = await moveSlidesToTrash(["slide1", "slide2"]);
 */

export function useMoveSlidesToTrash(lessonId: string | null) {
  const [moveSlidesToTrashMutation, { loading, error }] = useMutation(MoveSlidesToTrashDocument, {
    update(cache, { data }, { variables }) {
      if (!data?.moveSlidesToTrash) return;

      const trashedSlides = data.moveSlidesToTrash;
      const trashedCount = data.moveSlidesToTrash.length;
      const trashedIds = new Set(variables?.slideIds);

      try {
        cache.batch({
          update(cache) {
            const completeSlidesData = trashedSlides
              .map((slide) => {
                const slideId = cache.identify({ __typename: "Slide", id: slide.id });
                if (!slideId) return null;

                // Read the complete slide from cache
                const completeSlide = cache.readFragment({
                  id: slideId,
                  fragment: SlideFieldsFragmentDoc,
                });

                return completeSlide;
              })
              .filter(Boolean);

            // Update slide isTrash
            for (const slide of trashedSlides) {
              const slideId = cache.identify({ __typename: "Slide", id: slide.id });

              if (slideId) {
                cache.modify({
                  id: slideId,
                  fields: {
                    isTrash: () => true,
                  },
                });
              }
            }

            // Everything trashed is promoted to a main slide server-side, so detach it from its
            // former group here rather than waiting for a refetch.
            for (const slide of completeSlidesData) {
              if (!slide?.parentSlideId) continue;

              const slideId = cache.identify(slide);
              const parentId = cache.identify({ __typename: "Slide", id: slide.parentSlideId });
              if (!slideId || !parentId) continue;

              cache.modify({
                id: parentId,
                fields: {
                  children(existingData: readonly Reference[] | undefined, { readField }) {
                    return (existingData ?? []).filter(
                      (childRef) => readField<string>("id", childRef) !== slide.id
                    );
                  },
                },
              });
              cache.modify({ id: slideId, fields: { parentSlideId: () => null } });
            }

            // Remove trashed slides from active query
            cache.modify({
              fields: {
                slidesByLessonId(existingData, { readField, storeFieldName }) {
                  const lessonKey = `"lessonId":"${lessonId}"`;

                  if (!existingData?.items || !storeFieldName.includes(lessonKey))
                    return existingData;

                  const isActiveView = storeFieldName.includes('"isTrash":false');
                  const isTrashView = storeFieldName.includes('"isTrash":true');

                  // Remove slides from active view
                  if (isActiveView) {
                    const filteredItems = existingData.items.filter((slideRef: Reference) => {
                      const slideId = readField<string>("id", slideRef);
                      return slideId ? !trashedIds.has(slideId) : true;
                    });

                    return {
                      ...existingData,
                      items: filteredItems,
                      total: filteredItems.length,
                    };
                  }

                  // Add slides to trash view
                  if (isTrashView) {
                    const trashedRefs = completeSlidesData
                      .filter((slide) => slide !== null)
                      .map((slide) => cache.identify(slide))
                      .filter((id) => id !== null)
                      .map((id) => ({ __ref: id }));

                    const updatedItems = [...trashedRefs, ...existingData.items];

                    return {
                      ...existingData,
                      items: updatedItems,
                      total: updatedItems.length,
                    };
                  }

                  return existingData;
                },
              },
            });

            // Update lesson's trashCount
            if (lessonId) {
              cache.modify({
                id: `Lesson:${lessonId}`,
                fields: {
                  trashCount(existingData = 0) {
                    return existingData + trashedCount;
                  },
                },
              });
            }
          },
        });
      } catch (error) {
        console.error("Move slides to trash cache update failed:", error);
      }
    },
  });

  const moveSlidesToTrash = async (slideIds: string[], skipCheck: boolean) => {
    return withToast(
      async () => {
        const result = await moveSlidesToTrashMutation({ variables: { slideIds, skipCheck } });

        const trashedSlides = result.data?.moveSlidesToTrash ?? [];

        return trashedSlides;
      },
      {
        loading: `Moving ${slideIds.length} slide${slideIds.length !== 1 ? "s" : ""} to trash...`,
        success: `Successfully moved ${slideIds.length} slide${slideIds.length !== 1 ? "s" : ""} to trash`,
        error: "Failed to move slides to trash",
      },
      "moveSlidesToTrash"
    );
  };

  return { moveSlidesToTrash, loading, error };
}
